var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { Avatar, Box, Button, Step, StepLabel, Stepper, Typography, } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateTeam, useRetrieveTeam, useUpdateTeam } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { TeamForm } from "../teams/TeamForm";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { SearchTeamResults } from "../search/SearchTeamResults";
import { Welcome } from "../Welcome";
import { dismissOnboarding } from "./onboardingDismissal";
import { PlayerProfileStep } from "./PlayerProfileStep";
/**
 * The team-creation step. Reuses TeamForm, whose "You play for this team"
 * checkbox drives whether we then offer the player-profile step.
 */
var TeamStep = function (_a) {
    var teamId = _a.teamId, defaultPlaying = _a.defaultPlaying, onSaved = _a.onSaved;
    var _b = __read(useState(false), 2), error = _b[0], setError = _b[1];
    // Coming back after a successful create edits that team instead of making a
    // duplicate (e.g. to fix a typo).
    var isEditing = teamId !== null;
    var _c = useRetrieveTeam({
        id: teamId !== null && teamId !== void 0 ? teamId : undefined,
        enabled: isEditing,
    }), team = _c.data, isLoading = _c.isLoading, fetchError = _c.error;
    var createMutation = useCreateTeam({
        onMutate: function () { return setError(false); },
        onSuccess: function (data) { return onSaved(data.data.id); },
        onError: function () { return setError(true); },
    });
    var updateMutation = useUpdateTeam({
        id: teamId !== null && teamId !== void 0 ? teamId : undefined,
        onMutate: function () { return setError(false); },
        onSuccess: function () { return onSaved(teamId); },
        onError: function () { return setError(true); },
    });
    if (isEditing && isLoading) {
        return _jsx(Loading, {});
    }
    // If the just-created team can't be re-loaded, don't render a blank form that
    // would submit as an update — surface the error (the nav row still lets them
    // go back or leave onboarding).
    if (isEditing && fetchError) {
        return _jsx(ErrorView, { error: fetchError });
    }
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", sx: { fontWeight: 700 }, children: isEditing ? "Edit your team" : "Create your team" }), _jsx(TeamForm, { team: isEditing ? team : undefined, defaultAddAsPlayer: defaultPlaying, onSubmit: function (data) {
                    return isEditing ? updateMutation.mutate(data) : createMutation.mutate(data);
                } }), _jsx(ErrorSnackbar, { open: error, message: isEditing
                    ? "Failed to update team. Please try again."
                    : "Failed to create team. Please try again.", onClose: function () { return setError(false); } })] }));
};
/**
 * Explicit "are you playing?" step in the team flow. The answer pre-fills the
 * "You play for this team" toggle on the next step (which stays editable).
 */
var PlayingChoiceStep = function (_a) {
    var onChoose = _a.onChoose;
    return (_jsxs(Box, { sx: { textAlign: "center", py: 2 }, children: [_jsx(Avatar, { sx: {
                    width: 72,
                    height: 72,
                    mx: "auto",
                    mb: 2,
                    bgcolor: "rgba(22, 163, 74, 0.14)",
                    color: "#16A34A",
                }, children: _jsx(SportsSoccerIcon, { sx: { fontSize: 38 } }) }), _jsx(Typography, { variant: "h5", sx: { fontWeight: 700, mb: 1 }, children: "Will you play for this team too?" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 4 }, children: "We'll tick this on the next step so you can set up your player profile \u2014 you can change it there." }), _jsxs(Box, { sx: { display: "flex", justifyContent: "center", gap: 2 }, children: [_jsx(Button, { variant: "contained", onClick: function () { return onChoose(true); }, children: "Yes, I'll play" }), _jsx(Button, { variant: "outlined", onClick: function () { return onChoose(false); }, children: "No, just managing" })] })] }));
};
var STEP_LABELS = {
    team: ["Play?", "Team", "Profile"],
    player: ["Profile", "Find team"],
};
/**
 * The numbered, clickable step header (same treatment as the season-creation
 * wizard). Every step is reachable — onboarding is entirely optional — so any
 * label navigates straight to that step.
 */
var WizardStepper = function (_a) {
    var role = _a.role, step = _a.step, onStepClick = _a.onStepClick;
    return (_jsx(Stepper, { activeStep: step, alternativeLabel: true, sx: { mb: 4, "& .MuiStepLabel-label": { cursor: "pointer" } }, children: STEP_LABELS[role].map(function (label, index) { return (_jsx(Step, { completed: index < step, children: _jsx(StepLabel, { onClick: function () { return onStepClick(index); }, children: label }) }, label)); }) }));
};
/**
 * Post-signup onboarding, launched from Home for a brand-new user (no teams and
 * no leagues). Role-contextual: League Admins and Managers are never asked to
 * complete a player profile; the player-profile step only appears where it's
 * relevant, and is always skippable.
 */
export var OnboardingWizard = function (_a) {
    var player = _a.player, userPk = _a.userPk, onDismiss = _a.onDismiss;
    var navigate = useNavigate();
    // Step lives in the URL so the browser Back button walks the wizard.
    var _b = __read(useSearchParams(), 2), searchParams = _b[0], setSearchParams = _b[1];
    var roleParam = searchParams.get("role");
    var role = roleParam === "team" || roleParam === "player" ? roleParam : null;
    var step = Number(searchParams.get("step")) || 0;
    var playing = searchParams.get("playing") === "1";
    var teamIdParam = searchParams.get("teamId");
    var teamId = teamIdParam ? Number(teamIdParam) : null;
    // Step, the "playing" answer, and the created team id all live in the URL so
    // refresh, direct links, and the browser Back button all work.
    var setParams = function (updates) {
        var next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            return next.set(key, value);
        });
        setSearchParams(next);
    };
    var selectRole = function (selected) {
        return setSearchParams({ role: selected, step: "0" });
    };
    var goToStep = function (next) { return setParams({ step: String(next) }); };
    var onTeamSaved = function (id) {
        var teamIdStr = String(id);
        // Rewrite the current create step as an edit step (replace) so Back edits
        // the team instead of creating a duplicate, then push the profile step.
        var editStep = new URLSearchParams(searchParams);
        editStep.set("teamId", teamIdStr);
        setSearchParams(editStep, { replace: true });
        var profileStep = new URLSearchParams(searchParams);
        profileStep.set("step", "2");
        profileStep.set("teamId", teamIdStr);
        setSearchParams(profileStep);
    };
    // On any step change, jump to the top — a long previous step otherwise leaves
    // you scrolled into the middle of the next one.
    useEffect(function () {
        window.scrollTo({ top: 0 });
    }, [role, step]);
    var finishToTeam = function (id) {
        dismissOnboarding(userPk);
        navigate("/teams/".concat(id, "/"));
    };
    // End of the team flow: go to the created team if there is one, else just
    // leave onboarding — creating a team is optional too.
    var finishTeamFlow = function () {
        return teamId !== null ? finishToTeam(teamId) : onDismiss();
    };
    // From the find-team step: a player whose club isn't on the platform can
    // create it (becoming its manager) instead of dead-ending.
    var handleCreateTeam = function () {
        dismissOnboarding(userPk);
        navigate("/teams/create/");
    };
    var content;
    // "Next" — proceed past the current (optional) step to the next page. Set on
    // every step that has a next page, so nothing in onboarding is mandatory.
    var onSkipStep = null;
    // The final step of a flow: its primary action finishes onboarding, so the
    // redundant "Set up later" is hidden and the Next button reads "Finish".
    var isLastStep = false;
    if (role === null) {
        content = (_jsxs(_Fragment, { children: [_jsx(Welcome, { player: player, onSelectRole: selectRole }), _jsx(Box, { sx: { textAlign: "center", mt: 3 }, children: _jsx(Button, { onClick: onDismiss, children: "Set up later" }) })] }));
    }
    else if (role === "team") {
        if (step === 0) {
            content = (_jsx(PlayingChoiceStep, { onChoose: function (willPlay) {
                    return setParams({ step: "1", playing: willPlay ? "1" : "0" });
                } }));
            onSkipStep = function () { return goToStep(1); };
        }
        else if (step === 1) {
            content = (_jsx(TeamStep, { teamId: teamId, defaultPlaying: playing, onSaved: onTeamSaved }));
            onSkipStep = function () { return goToStep(2); };
        }
        else {
            content = (_jsx(PlayerProfileStep, { player: player, submitLabel: "Save & finish", fullProfile: playing, onDone: finishTeamFlow }));
            onSkipStep = finishTeamFlow;
            isLastStep = true;
        }
    }
    else if (step === 0) {
        content = (_jsx(PlayerProfileStep, { player: player, submitLabel: "Save & continue", fullProfile: true, onDone: function () { return goToStep(1); } }));
        onSkipStep = function () { return goToStep(1); };
    }
    else {
        content = _jsx(SearchTeamResults, { onCreateTeam: handleCreateTeam });
        onSkipStep = onDismiss;
        isLastStep = true;
    }
    return (_jsxs(Box, { sx: { maxWidth: role === null ? 960 : 640, mx: "auto" }, children: [role !== null && (_jsx(WizardStepper, { role: role, step: step, onStepClick: goToStep })), content, role !== null && (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                            mt: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }, children: [_jsx(Button, { size: "small", onClick: function () { return navigate(-1); }, startIcon: _jsx(KeyboardArrowLeftIcon, {}), children: "Back" }), onSkipStep && (_jsx(Button, { size: "small", onClick: onSkipStep, endIcon: _jsx(KeyboardArrowRightIcon, {}), children: isLastStep ? "Finish" : "Next" }))] }), !isLastStep && (_jsx(Box, { sx: { textAlign: "center", mt: 1 }, children: _jsx(Button, { size: "small", color: "inherit", onClick: onDismiss, children: "Set up later" }) }))] }))] }));
};
